/**
 * AxLabs Mathematics Experiments - Optimization
 *
 * Collection of experiments exploring optimization techniques and problems:
 * linear programming, gradient descent, constraint satisfaction,
 * game theory, simplex method, and convexity analysis.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// LINEAR PROGRAMMING FEASIBILITY
// ============================================================================
export const LinearProgrammingFeasibility = createExperimentFromSchema({
    id: 'linear-programming-feasibility',
    name: 'Linear Programming Feasibility',
    domain: 'mathematics',
    description: 'Explore feasible regions in linear programming',
    conceptTags: ['Linear Programming', 'Feasible Region', 'Constraints', 'Optimization'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Variable x', unit: 'dimensionless', initial: 2, symbol: 'x' },
        { id: 'y', name: 'Variable y', unit: 'dimensionless', initial: 2, symbol: 'y' },
        { id: 'objective', name: 'Objective Value', unit: 'dimensionless', initial: 8, symbol: 'Z' },
        { id: 'feasible', name: 'Is Feasible', unit: 'dimensionless', initial: 1, symbol: 'F' },
        { id: 'constraint1', name: 'Constraint 1 LHS', unit: 'dimensionless', initial: 4, symbol: 'C_1' },
    ],
    parameters: [
        { id: 'xTarget', name: 'Target x', unit: 'dimensionless', min: 0, max: 10, default: 2, symbol: 'x' },
        { id: 'yTarget', name: 'Target y', unit: 'dimensionless', min: 0, max: 10, default: 3, symbol: 'y' },
    ],
    equations: [
        {
            id: 'lp-objective',
            name: 'Objective Function',
            latex: '\\max Z = 2x + 3y',
            description: 'Maximize linear objective function',
        },
        {
            id: 'lp-constraints',
            name: 'Constraints',
            latex: 'x + y \\leq 5, \\quad x \\geq 0, \\quad y \\geq 0',
            description: 'Linear constraints define feasible region',
        },
    ],
    derivatives: (_state, params) => {
        const x = getParam(params, 'xTarget');
        const y = getParam(params, 'yTarget');
        const objective = 2 * x + 3 * y;
        const feasible = (x + y <= 5 && x >= 0 && y >= 0) ? 1 : 0;
        const constraint1 = x + y;
        return {
            x: x - getState(_state, 'x'),
            y: y - getState(_state, 'y'),
            objective: objective - getState(_state, 'objective'),
            feasible: feasible - getState(_state, 'feasible'),
            constraint1: constraint1 - getState(_state, 'constraint1'),
        };
    },
    bounds: {
        x: { min: 0, max: 10 },
        y: { min: 0, max: 10 },
        objective: { min: 0, max: 50 },
    },
});
// ============================================================================
// GRADIENT DESCENT CONVERGENCE
// ============================================================================
export const GradientDescentConvergence = createExperimentFromSchema({
    id: 'gradient-descent-convergence',
    name: 'Gradient Descent Convergence',
    domain: 'mathematics',
    description: 'Visualize gradient descent optimization',
    conceptTags: ['Gradient Descent', 'Optimization', 'Convergence', 'Learning Rate'],
    difficulty: 6,
    stateVariables: [
        { id: 'x', name: 'Current Position x', unit: 'dimensionless', initial: 2, symbol: 'x_n' },
        { id: 'gradient', name: 'Gradient', unit: 'dimensionless', initial: 4, symbol: '\\nabla f' },
        { id: 'step', name: 'Step Size', unit: 'dimensionless', initial: 0.4, symbol: '\\Delta x' },
        { id: 'functionValue', name: 'f(x)', unit: 'dimensionless', initial: 5, symbol: 'f(x)' },
        { id: 'iteration', name: 'Iteration', unit: 'dimensionless', initial: 1, symbol: 'n' },
    ],
    parameters: [
        { id: 'currentX', name: 'Current x', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'x' },
        { id: 'learningRate', name: 'Learning Rate', unit: 'dimensionless', min: 0.01, max: 0.5, default: 0.1, symbol: '\\alpha' },
    ],
    equations: [
        {
            id: 'gradient-descent-rule',
            name: 'Gradient Descent Update',
            latex: 'x_{n+1} = x_n - \\alpha \\nabla f(x_n)',
            description: 'Update rule moving opposite to gradient',
        },
        {
            id: 'quadratic-gradient',
            name: 'Gradient of f(x) = x²',
            latex: '\\nabla f(x) = 2x',
            description: 'Derivative gives direction of steepest ascent',
        },
    ],
    derivatives: (_state, params) => {
        const x = getParam(params, 'currentX');
        const alpha = getParam(params, 'learningRate');
        // For f(x) = x², gradient = 2x
        const gradient = 2 * x;
        const step = alpha * gradient;
        const functionValue = x * x;
        return {
            x: x - getState(_state, 'x'),
            gradient: gradient - getState(_state, 'gradient'),
            step: step - getState(_state, 'step'),
            functionValue: functionValue - getState(_state, 'functionValue'),
            iteration: 1,
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        gradient: { min: -20, max: 20 },
        functionValue: { min: 0, max: 100 },
    },
});
// ============================================================================
// CONSTRAINT SATISFACTION
// ============================================================================
export const ConstraintSatisfaction = createExperimentFromSchema({
    id: 'constraint-satisfaction',
    name: 'Constraint Satisfaction',
    domain: 'mathematics',
    description: 'Find solutions satisfying multiple constraints',
    conceptTags: ['Constraints', 'Feasibility', 'Solution Space', 'Inequalities'],
    difficulty: 5,
    stateVariables: [
        { id: 'x', name: 'Variable x', unit: 'dimensionless', initial: 3, symbol: 'x' },
        { id: 'y', name: 'Variable y', unit: 'dimensionless', initial: 2, symbol: 'y' },
        { id: 'sat1', name: 'Satisfies C1', unit: 'dimensionless', initial: 1, symbol: 'S_1' },
        { id: 'sat2', name: 'Satisfies C2', unit: 'dimensionless', initial: 1, symbol: 'S_2' },
        { id: 'satAll', name: 'Satisfies All', unit: 'dimensionless', initial: 1, symbol: 'S_{all}' },
    ],
    parameters: [
        { id: 'xVal', name: 'x value', unit: 'dimensionless', min: 0, max: 10, default: 3, symbol: 'x' },
        { id: 'yVal', name: 'y value', unit: 'dimensionless', min: 0, max: 10, default: 2, symbol: 'y' },
    ],
    equations: [
        {
            id: 'constraint-system',
            name: 'Constraint System',
            latex: 'x + 2y \\leq 7, \\quad 2x - y \\geq 2, \\quad x, y \\geq 0',
            description: 'System of linear inequality constraints',
        },
        {
            id: 'feasible-set',
            name: 'Feasible Set',
            latex: 'S = \\{(x,y) \\in \\mathbb{R}^2 : g_i(x,y) \\leq 0\\}',
            description: 'Set of all points satisfying constraints',
        },
    ],
    derivatives: (_state, params) => {
        const x = getParam(params, 'xVal');
        const y = getParam(params, 'yVal');
        const sat1 = (x + 2 * y <= 7 && x >= 0 && y >= 0) ? 1 : 0;
        const sat2 = (2 * x - y >= 2) ? 1 : 0;
        const satAll = (sat1 && sat2) ? 1 : 0;
        return {
            x: x - getState(_state, 'x'),
            y: y - getState(_state, 'y'),
            sat1: sat1 - getState(_state, 'sat1'),
            sat2: sat2 - getState(_state, 'sat2'),
            satAll: satAll - getState(_state, 'satAll'),
        };
    },
    bounds: {
        x: { min: 0, max: 10 },
        y: { min: 0, max: 10 },
    },
});
// ============================================================================
// MINIMAX GAME THEORY
// ============================================================================
export const MinimaxGameTheory = createExperimentFromSchema({
    id: 'minimax-game-theory',
    name: 'Minimax Game Theory',
    domain: 'mathematics',
    description: 'Explore optimal strategies in zero-sum games',
    conceptTags: ['Game Theory', 'Minimax', 'Zero-Sum Games', 'Nash Equilibrium'],
    difficulty: 6,
    stateVariables: [
        { id: 'rowStrategy', name: 'Row Player Mixed', unit: 'dimensionless', initial: 0.5, symbol: 'p' },
        { id: 'colStrategy', name: 'Col Player Mixed', unit: 'dimensionless', initial: 0.5, symbol: 'q' },
        { id: 'rowPayoff', name: 'Row Expected', unit: 'dimensionless', initial: 0, symbol: 'E_R' },
        { id: 'colPayoff', name: 'Col Expected', unit: 'dimensionless', initial: 0, symbol: 'E_C' },
        { id: 'gameValue', name: 'Game Value', unit: 'dimensionless', initial: 0, symbol: 'v' },
    ],
    parameters: [
        { id: 'p', name: 'Row prob p', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: 'p' },
        { id: 'q', name: 'Col prob q', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: 'q' },
    ],
    equations: [
        {
            id: 'expected-payoff',
            name: 'Expected Payoff',
            latex: 'E(p,q) = \\sum_{i,j} p_i q_j a_{ij}',
            description: 'Expected value for mixed strategies',
        },
        {
            id: 'minimax-theorem',
            name: 'Minimax Theorem',
            latex: '\\max_p \\min_q E(p,q) = \\min_q \\max_p E(p,q)',
            description: 'In zero-sum games, maximin equals minimax',
        },
    ],
    derivatives: (_state, params) => {
        const p = getParam(params, 'p');
        const q = getParam(params, 'q');
        // Payoff matrix for matching pennies: [[1,-1],[-1,1]]
        // Row wants to maximize, column wants to minimize
        const rowPayoff = p * q * 1 + p * (1 - q) * (-1) + (1 - p) * q * (-1) + (1 - p) * (1 - q) * 1;
        const colPayoff = -rowPayoff; // Zero-sum
        const gameValue = rowPayoff;
        return {
            rowStrategy: p - getState(_state, 'rowStrategy'),
            colStrategy: q - getState(_state, 'colStrategy'),
            rowPayoff: rowPayoff - getState(_state, 'rowPayoff'),
            colPayoff: colPayoff - getState(_state, 'colPayoff'),
            gameValue: gameValue - getState(_state, 'gameValue'),
        };
    },
    bounds: {
        rowStrategy: { min: 0, max: 1 },
        colStrategy: { min: 0, max: 1 },
        rowPayoff: { min: -2, max: 2 },
        colPayoff: { min: -2, max: 2 },
        gameValue: { min: -2, max: 2 },
    },
});
// ============================================================================
// SIMPLEX METHOD ITERATION
// ============================================================================
export const SimplexMethodIteration = createExperimentFromSchema({
    id: 'simplex-method-iteration',
    name: 'Simplex Method Iteration',
    domain: 'mathematics',
    description: 'Step through the simplex algorithm',
    conceptTags: ['Simplex Method', 'Linear Programming', 'Pivot Operation', 'Basis'],
    difficulty: 7,
    stateVariables: [
        { id: 'pivotRow', name: 'Pivot Row', unit: 'dimensionless', initial: 1, symbol: 'r' },
        { id: 'pivotCol', name: 'Pivot Column', unit: 'dimensionless', initial: 0, symbol: 'c' },
        { id: 'pivotValue', name: 'Pivot Element', unit: 'dimensionless', initial: 2, symbol: 'a_{rc}' },
        { id: 'objective', name: 'Objective Value', unit: 'dimensionless', initial: 10, symbol: 'Z' },
        { id: 'iteration', name: 'Iteration', unit: 'dimensionless', initial: 1, symbol: 'k' },
    ],
    parameters: [
        { id: 'iter', name: 'Iteration Step', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'k' },
    ],
    equations: [
        {
            id: 'simplex-pivot',
            name: 'Pivot Operation',
            latex: 'a_{ij}\' = a_{ij} - \\frac{a_{rj} \\cdot a_{ic}}{a_{rc}}',
            description: 'Row operation to make pivot element 1',
        },
        {
            id: 'simplex-entering',
            name: 'Entering Variable',
            latex: '\\text{Enter: } j = \\arg\\max_k \\{c_k : c_k > 0\\}',
            description: 'Choose variable with most positive reduced cost',
        },
    ],
    derivatives: (_state, params) => {
        const iter = Math.round(getParam(params, 'iter'));
        // Simulated simplex iterations
        const iterations = [
            { pivotRow: 1, pivotCol: 0, pivotValue: 2, objective: 10 },
            { pivotRow: 0, pivotCol: 1, pivotValue: 3, objective: 15 },
            { pivotRow: 2, pivotCol: 2, pivotValue: 1, objective: 18 },
            { pivotRow: 0, pivotCol: 0, pivotValue: 4, objective: 20 },
        ];
        const iterData = iterations[iter] ?? iterations[0];
        return {
            pivotRow: (iterData?.pivotRow ?? 0) - getState(_state, 'pivotRow'),
            pivotCol: (iterData?.pivotCol ?? 0) - getState(_state, 'pivotCol'),
            pivotValue: (iterData?.pivotValue ?? 0) - getState(_state, 'pivotValue'),
            objective: (iterData?.objective ?? 0) - getState(_state, 'objective'),
            iteration: 1,
        };
    },
    bounds: {
        pivotValue: { min: 0, max: 10 },
        objective: { min: 0, max: 50 },
    },
});
// ============================================================================
// CONVEXITY ANALYSIS
// ============================================================================
export const ConvexityAnalysis = createExperimentFromSchema({
    id: 'convexity-analysis',
    name: 'Convexity Analysis',
    domain: 'mathematics',
    description: 'Analyze convexity of functions and sets',
    conceptTags: ['Convexity', 'Jensen Inequality', 'Convex Functions', 'Optimization'],
    difficulty: 6,
    stateVariables: [
        { id: 'x', name: 'Point x', unit: 'dimensionless', initial: 2, symbol: 'x' },
        { id: 'y', name: 'Point y', unit: 'dimensionless', initial: 4, symbol: 'y' },
        { id: 'lambda', name: 'Weight λ', unit: 'dimensionless', initial: 0.5, symbol: '\\lambda' },
        { id: 'lhs', name: 'f(λx+(1-λ)y)', unit: 'dimensionless', initial: 9, symbol: 'LHS' },
        { id: 'rhs', name: 'λf(x)+(1-λ)f(y)', unit: 'dimensionless', initial: 10, symbol: 'RHS' },
    ],
    parameters: [
        { id: 'xVal', name: 'x value', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'x' },
        { id: 'yVal', name: 'y value', unit: 'dimensionless', min: -5, max: 5, default: 4, symbol: 'y' },
        { id: 'lambdaVal', name: 'λ value', unit: 'dimensionless', min: 0, max: 1, default: 0.5, symbol: '\\lambda' },
    ],
    equations: [
        {
            id: 'convex-definition',
            name: 'Convex Function Definition',
            latex: 'f(\\lambda x + (1-\\lambda)y) \\leq \\lambda f(x) + (1-\\lambda)f(y)',
            description: 'Function lies below chord between any two points',
        },
        {
            id: 'jensen-inequality',
            name: 'Jensen Inequality',
            latex: 'f(\\mathbb{E}[X]) \\leq \\mathbb{E}[f(X)]',
            description: 'For convex f, function of expectation ≤ expectation of function',
        },
    ],
    derivatives: (_state, params) => {
        const x = getParam(params, 'xVal');
        const y = getParam(params, 'yVal');
        const lambda = getParam(params, 'lambdaVal');
        // For f(x) = x² (convex)
        const convexPoint = lambda * x + (1 - lambda) * y;
        const lhs = convexPoint * convexPoint;
        const rhs = lambda * x * x + (1 - lambda) * y * y;
        return {
            x: x - getState(_state, 'x'),
            y: y - getState(_state, 'y'),
            lambda: lambda - getState(_state, 'lambda'),
            lhs: lhs - getState(_state, 'lhs'),
            rhs: rhs - getState(_state, 'rhs'),
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -10, max: 10 },
        lhs: { min: 0, max: 100 },
        rhs: { min: 0, max: 100 },
    },
});
// ============================================================================
// EXPORT ALL OPTIMIZATION EXPERIMENTS
// ============================================================================
export const ALL_OPTIMIZATION = [
    LinearProgrammingFeasibility,
    GradientDescentConvergence,
    ConstraintSatisfaction,
    MinimaxGameTheory,
    SimplexMethodIteration,
    ConvexityAnalysis,
];
//# sourceMappingURL=optimization.js.map